import { lazy, type ComponentType } from 'react';

import { requestChunkReload } from '#features/error/utils/chunkReload';
import { isChunkLoadError } from '#utils/isChunkLoadError';

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- props are irrelevant here
type LazyComponent = ComponentType<any>;
type LazyModule<T> = { default: T };
type LazyImporter<T extends LazyComponent> = () => Promise<LazyModule<T>>;

/**
 * Reading a named export straight off the import (`(await import(…)).Foo`) throws a plain
 * TypeError when the chunk never arrives — the module is undefined, and the message names the
 * export, so `isChunkLoadError` misses it and the reload-once recovery never runs. Going through
 * here turns that into a message the chunk-error check recognises.
 */
export const resolveNamedExport = <T>(
  module: Record<string, unknown> | undefined,
  exportName: string
): T => {
  const value = module?.[exportName] as T | undefined;

  if (!value) {
    throw new Error(`Failed to load module script: missing export "${exportName}"`);
  }

  return value;
};

export const lazyWithRetry = <T extends LazyComponent>(importer: LazyImporter<T>) =>
  lazy(() =>
    importer().catch(async (error) => {
      if (typeof window === 'undefined' || !isChunkLoadError(error)) {
        throw error;
      }

      // Своей защёлки тут больше нет: она была третьей независимой, то есть давала ещё одну
      // перезагрузку сверх общего бюджета. Решение — общее на вкладку.
      if (
        await requestChunkReload({
          storage: sessionStorage,
          reload: () => window.location.reload(),
        })
      ) {
        // Перезагрузка назначена — держим Suspense до неё, показывать ошибку уже незачем.
        return new Promise<never>(() => {});
      }

      throw error;
    })
  );

/** {@link lazyWithRetry} for components published under a named export. */
export const lazyNamedWithRetry = <T extends LazyComponent>(
  importer: () => Promise<Record<string, unknown> | undefined>,
  exportName: string
) =>
  lazyWithRetry<T>(async () => ({ default: resolveNamedExport<T>(await importer(), exportName) }));
